/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

import { useEffect, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UISelect from 'UIComponents/input/UISelect';
import UILink from 'UIComponents/link/UILink';
import userInfo from 'hub-http/userInfo';
import { ALL_BUSINESS_UNIT_ID } from '../constants/BusinessUnit';
import { getHasBusinessUnitsViewAllScope, getHasBusinessUnitUsersAccess, getHasBusinessUnitsWriteScope } from '../redux/selectors';
import { makeBusinessUnitSelectOption } from './BusinessUnitOptions';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getIsBusinessUnitOneOption = ({
  formattedOptions
}) => {
  //If there is only one option, or effectively only one option
  //due to the inclusion of view all,
  //readonly will be true.
  return formattedOptions && formattedOptions.length <= 1;
};
const getDefaultValue = ({
  buId,
  buIds,
  multi,
  includeViewAll,
  canViewAll,
  formattedOptions,
  isBusinessUnitOneOption,
  isLoading,
  hasFailed
}) => {
  // If formattedOptions is an empty array, don't return the buId or anything else
  if (isLoading || hasFailed || formattedOptions.length === 0) {
    return undefined;
  }
  if (multi) {
    return buIds;
  }
  if (includeViewAll && buId === undefined && canViewAll) {
    // Edge case for "All business units" option
    return ALL_BUSINESS_UNIT_ID;
  }
  if (isBusinessUnitOneOption) {
    // If there is only one option, or effectively only one option
    // due to the inclusion of view all,
    // show the option that is not view all
    const firstOption = formattedOptions.find(formattedOption => formattedOption.value !== ALL_BUSINESS_UNIT_ID);
    return firstOption && firstOption.value;
  }
  const buIdExists = formattedOptions.find(formattedOption => formattedOption.value === buId);
  if (!buIdExists) {
    return undefined;
  }
  return buId;
};
const getFormattedOptions = (buOptions, includeAccountBusinessUnit, showOptionImage) => {
  let finalBuOptions = buOptions;
  if (!includeAccountBusinessUnit) {
    finalBuOptions = buOptions.filter(buOption => buOption.id !== 0);
  }
  return finalBuOptions.map(buOption => makeBusinessUnitSelectOption(buOption, showOptionImage));
};
const Placeholder = ({
  hasFailed,
  isLoading,
  multi,
  options
}) => {
  if (hasFailed) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ui-business-units-lib.options.error.title"
    });
  }
  if (isLoading) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ui-business-units-lib.options.loading"
    });
  }
  if (options.length === 0) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ui-business-units-lib.placeholders.selectBusinessUnits_buv2.none"
    });
  }
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "ui-business-units-lib.placeholders.selectBusinessUnits_buv2",
    options: {
      count: multi ? 2 : 1
    }
  });
};
const BusinessUnitSelect = ({
  onChange,
  buOptions = [],
  buId,
  buIds,
  buttonUse,
  multi = false,
  withHeader = false,
  includeViewAll = false,
  className,
  includeAccountBusinessUnit = true,
  menuWidth,
  readOnly = false,
  isLoading = false,
  hasFailed = false,
  disabled = false,
  placement,
  ButtonContent,
  showFooter = true,
  showOptionImage = true
}) => {
  const [userScopes, setUserScopes] = useState([]);
  const [gates, setGates] = useState([]);
  const [portalId, setPortalId] = useState(0);
  const hasBusinessUnitUsersAccess = getHasBusinessUnitUsersAccess(gates);
  const hasViewAllScope = getHasBusinessUnitsViewAllScope(userScopes);
  const hasWriteScope = getHasBusinessUnitsWriteScope(userScopes);
  const canViewAll = !hasBusinessUnitUsersAccess || hasBusinessUnitUsersAccess && hasViewAllScope;
  useEffect(() => {
    userInfo().then(authData => {
      setUserScopes(authData.user.scopes);
      setGates(authData.gates);
      setPortalId(authData.portal.portal_id);
    }).catch(error => console.error(error));
  }, []);
  const formattedOptions = getFormattedOptions(buOptions, includeAccountBusinessUnit, showOptionImage);
  if (includeViewAll && canViewAll) {
    formattedOptions.unshift({
      value: ALL_BUSINESS_UNIT_ID,
      text: I18n.text('ui-business-units-lib.labels.allBusinessUnits')
    });
  }
  const handleOnChange = ({
    currentTarget: {
      value
    }
  }) => {
    if (includeViewAll && value === ALL_BUSINESS_UNIT_ID) {
      // Edge case for "All business units" option
      onChange(undefined);
    } else {
      onChange(value);
    }
  };
  const isBusinessUnitOneOption = getIsBusinessUnitOneOption({
    formattedOptions
  });
  const defaultValue = getDefaultValue({
    multi,
    buId,
    buIds,
    includeViewAll,
    canViewAll,
    formattedOptions,
    isBusinessUnitOneOption,
    isLoading,
    hasFailed
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [withHeader && /*#__PURE__*/_jsx(UIFormLabel, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ui-business-units-lib.labels.businessUnit"
      })
    }), /*#__PURE__*/_jsx(UIFormControl, {
      error: hasFailed,
      validationMessage: hasFailed && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ui-business-units-lib.options.error.message"
      }),
      children: /*#__PURE__*/_jsx(UISelect, {
        "data-test-id": "business-unit-select",
        options: formattedOptions,
        onChange: handleOnChange,
        value: defaultValue,
        buttonUse: buttonUse,
        multi: multi,
        placeholder: /*#__PURE__*/_jsx(Placeholder, {
          hasFailed: hasFailed,
          isLoading: isLoading,
          multi: multi,
          options: formattedOptions
        }),
        menuWidth: menuWidth,
        className: className,
        readOnly: isBusinessUnitOneOption || readOnly,
        isLoading: isLoading,
        error: hasFailed,
        disabled: disabled,
        placement: placement,
        ButtonContent: ButtonContent,
        anchorType: multi ? 'button' : undefined,
        dropdownFooter: showFooter && hasWriteScope && /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: `/settings/${portalId}/business-units`,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-business-units-lib.footer.manageBUs"
          })
        })
      })
    })]
  });
};
export default BusinessUnitSelect;